// src/App.tsx
'use client';

import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { getUserRole } from "@/lib/auth";
import { getSupabase } from "@/lib/supabase";

// MODAL PROVIDER — THIS FIXES ALL MODAL ISSUES 
import { ModalProvider } from "@/context/ModalContext";

// ✨ USE SIMPLE AUTH LOADER (not the full DashboardLoader)
import AuthLoader from "@/components/AuthLoader";

// PUBLIC PAGES
import Landing from "@/pages/Landing";
import Signup from "@/pages/Signup";
import CheckEmail from "@/pages/CheckEmail";
import AboutYou from "@/pages/AboutYou";
import BasicInfo from "@/pages/BasicInfo";
import ActivityLevel from "@/pages/ActivityLevel";
import PrimarySport from "@/pages/PrimarySport";
import SecondarySport from "@/pages/SecondarySport";
import HealthPrefs from "@/pages/HealthPrefs";
import DeviceIntegrations from "@/pages/DeviceIntegrations";
import CreateGoal from "@/pages/CreateGoal";
import Payment from "@/pages/Payment";
import Success from "@/pages/Success";
import Nutrition from "@/pages/Nutrition";
import Training from "@/pages/Training";
import Leaderboard from "@/pages/Leaderboard";
import Dashboard from "@/pages/Dashboard";
import Settings from "@/pages/Settings";
import TrainingProgram from "@/pages/TrainingProgram";
import NutritionPlan from "@/pages/NutritionPlan";
import JoinFriend from '@/pages/JoinFriend';
import JoinChallenge from '@/pages/JoinChallenge';
import Billing from "@/pages/Billing";
import Login from "@/pages/Login";
import VerifyPhone from "@/pages/VerifyPhone";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import Contact from "@/pages/Contact";
import PartnerWaitlist from "@/pages/PartnerWaitlist";
import Waitlist from "@/pages/Waitlist";
import WhoopOAuthCallback from "@/pages/WhoopOAuthCallback";
import HowItWorks from "@/pages/HowItWorks";
import MurphPartnership from "@/pages/MurphPartnership";
import CrossfitWessex from "@/pages/CrossfitWessex";

// PARTNER PUBLIC
import PartnerSignup from "@/pages/PartnerSignup";
import PartnerCheckEmail from "@/pages/PartnerCheckEmail";
import PartnerBusinessInfo from "@/pages/PartnerBusinessInfo";
import PartnerStripeSuccess from "@/pages/PartnerStripeSuccess";

// LAZY LOADED
const PartnerChooseTier = lazy(() => import("@/pages/PartnerChooseTier"));
const PartnerStripeConnect = lazy(() => import("@/pages/PartnerStripeConnect"));
const PartnerDashboard = lazy(() => import("@/pages/PartnerDashboard"));
const PartnerBilling = lazy(() => import("@/pages/PartnerBilling"));
const PartnerSettings = lazy(() => import("@/pages/PartnerSettings"));
const PartnerTrainingBuilder = lazy(() => import("@/pages/PartnerTrainingBuilder"));
const PartnerNutritionBuilder = lazy(() => import("@/pages/PartnerNutritionBuilder"));
const PartnerClientProgress = lazy(() => import("@/pages/PartnerClientProgress"));
const PartnerClientCalendar = lazy(() => import("@/pages/PartnerClientCalendar"));
const PartnerSupplements = lazy(() => import("@/pages/PartnerSupplements"));
const PartnerMacroTargets = lazy(() => import("@/pages/PartnerMacroTargets"));
const PartnerClientOnboarding = lazy(() => import("@/pages/PartnerClientOnboarding"));
const PartnerAnalytics = lazy(() => import("@/pages/PartnerAnalytics"));
const PartnerWorkoutStore = lazy(() => import("@/pages/PartnerWorkoutStore"));
const PartnerCreateWorkout = lazy(() => import("@/pages/PartnerCreateWorkout"));

// PUBLIC — Workout Purchase Pages
import BuyWorkout from "@/pages/BuyWorkout";
import WorkoutPurchaseSuccess from "@/pages/WorkoutPurchaseSuccess";

// ADMIN (lazy loaded)
const AdminOverview = lazy(() => import("@/pages/AdminOverview"));
const AdminEngagement = lazy(() => import("@/pages/AdminEngagement"));
const AdminFeatureUsage = lazy(() => import("@/pages/AdminFeatureUsage"));
const AdminRetention = lazy(() => import("@/pages/AdminRetention"));
const AdminAIQuality = lazy(() => import("@/pages/AdminAIQuality"));

// ✨ UPDATED PROTECTED ROUTE WITH LOADER
function ProtectedRoute({ children, requirePartner = false }: { children: React.ReactNode; requirePartner?: boolean }) {
  const navigate = useNavigate();
  const [ready, setReady] = React.useState(false);
  const [allowed, setAllowed] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      const supabase = getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const role = await getUserRole();

      if (session) {
        if (requirePartner && role !== "partner") {
          navigate("/dashboard", { replace: true });
          return;
        }
        if (!requirePartner && role === "partner") {
          navigate("/partner/dashboard", { replace: true });
          return;
        }
        setAllowed(true);
        setReady(true);
        return;
      }
      navigate("/login", { replace: true });
    })();
  }, [navigate, requirePartner]);

  // ✨ USE THE SIMPLE AUTH LOADER INSTEAD
  if (!ready) {
    return <AuthLoader />;
  }
  
  return allowed ? <>{children}</> : null;
}

export default function App() {
  return (
    <ModalProvider>
      <Routes>
        {/* PUBLIC */}
        <Route path="/" element={<Landing />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/check-email" element={<CheckEmail />} />
        <Route path="/login" element={<Login />} />
        <Route path="/waitlist" element={<Waitlist />} />
        <Route path="/verify-phone" element={<VerifyPhone />} />
        

        {/* PARTNER PUBLIC */}
        <Route path="/partner/signup" element={<PartnerSignup />} />
        <Route path="/partner/waitlist" element={<PartnerWaitlist />} />
        <Route path="/partner/check-email" element={<PartnerCheckEmail />} />
        <Route path="/partner/business-info" element={<ProtectedRoute><PartnerBusinessInfo /></ProtectedRoute>} />
        <Route path="/partner/choose-tier" element={<Suspense fallback={<div className="text-[#0f172a] p-8">Loading...</div>}><ProtectedRoute><PartnerChooseTier /></ProtectedRoute></Suspense>} />
        <Route path="/partner/stripe-success" element={<PartnerStripeSuccess />} />

        {/* WORKOUT STORE — PUBLIC PURCHASE PAGES */}
        <Route path="/buy/success" element={<WorkoutPurchaseSuccess />} />
        <Route path="/buy/:shareCode" element={<BuyWorkout />} />

        {/* LEGAL */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/how" element={<HowItWorks />} />
        <Route path="/murph" element={<MurphPartnership />} />
        <Route path="/crossfit-wessex" element={<CrossfitWessex />} />

        {/* CLIENT ONBOARDING */}
        <Route path="/basic-info" element={<ProtectedRoute><BasicInfo /></ProtectedRoute>} />
        <Route path="/about-you" element={<AboutYou />} />
        <Route path="/activity-level" element={<ProtectedRoute><ActivityLevel /></ProtectedRoute>} />
        <Route path="/primary-sport" element={<ProtectedRoute><PrimarySport /></ProtectedRoute>} />
        <Route path="/secondary-sport" element={<ProtectedRoute><SecondarySport /></ProtectedRoute>} />
        <Route path="/health-prefs" element={<ProtectedRoute><HealthPrefs /></ProtectedRoute>} />
        <Route path="/create-goal" element={<ProtectedRoute><CreateGoal /></ProtectedRoute>} />
        <Route path="/device-integrations" element={<ProtectedRoute><DeviceIntegrations /></ProtectedRoute>} />
        <Route path="/payment" element={<ProtectedRoute><Payment /></ProtectedRoute>} />
        <Route path="/success" element={<ProtectedRoute><Success /></ProtectedRoute>} />

        {/* MAIN APP */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
        <Route path="/nutrition" element={<ProtectedRoute><Nutrition /></ProtectedRoute>} />
        <Route path="/training" element={<ProtectedRoute><Training /></ProtectedRoute>} />
        <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/training-program" element={<ProtectedRoute><TrainingProgram /></ProtectedRoute>} />
        <Route path="/nutrition-plan" element={<ProtectedRoute><NutritionPlan /></ProtectedRoute>} /> 
        <Route path="/join/:userId" element={<JoinFriend />} />
        <Route path="/join-challenge/:challengeId" element={<JoinChallenge />} />

        {/* PARTNER DASHBOARD */}
        <Route path="/partner/stripe-connect" element={<Suspense fallback={<div className="text-[#0f172a] p-8">Loading...</div>}><ProtectedRoute><PartnerStripeConnect /></ProtectedRoute></Suspense>} />
        <Route path="/partner/dashboard" element={<Suspense fallback={<div className="text-[#0f172a] p-8">Loading...</div>}><ProtectedRoute requirePartner><PartnerDashboard /></ProtectedRoute></Suspense>} />
        <Route path="/partner/billing" element={<Suspense fallback={<div className="text-[#0f172a] p-8">Loading...</div>}><ProtectedRoute requirePartner><PartnerBilling /></ProtectedRoute></Suspense>} />
        <Route path="/partner/settings" element={<Suspense fallback={<div className="text-[#0f172a] p-8">Loading...</div>}><ProtectedRoute requirePartner><PartnerSettings /></ProtectedRoute></Suspense>} />
        <Route path="/partner/training-builder" element={<Suspense fallback={<div className="text-[#0f172a] p-8">Loading...</div>}><ProtectedRoute requirePartner><PartnerTrainingBuilder /></ProtectedRoute></Suspense>} />
        <Route path="/partner/nutrition-builder" element={<Suspense fallback={<div className="text-[#0f172a] p-8">Loading...</div>}><ProtectedRoute requirePartner><PartnerNutritionBuilder /></ProtectedRoute></Suspense>} />
        <Route path="/partner/client-progress" element={<Suspense fallback={<div className="text-[#0f172a] p-8">Loading...</div>}><ProtectedRoute requirePartner><PartnerClientProgress /></ProtectedRoute></Suspense>} />
        <Route path="/partner/client-calendar" element={<Suspense fallback={<div className="text-[#0f172a] p-8">Loading...</div>}><ProtectedRoute requirePartner><PartnerClientCalendar /></ProtectedRoute></Suspense>} />
        <Route path="/partner/supplements" element={<Suspense fallback={<div className="text-[#0f172a] p-8">Loading...</div>}><ProtectedRoute requirePartner><PartnerSupplements /></ProtectedRoute></Suspense>} />
        <Route path="/partner/macro-targets" element={<Suspense fallback={<div className="text-[#0f172a] p-8">Loading...</div>}><ProtectedRoute requirePartner><PartnerMacroTargets /></ProtectedRoute></Suspense>} />
        <Route path="/partner/client-onboarding" element={<Suspense fallback={<div className="text-[#0f172a] p-8">Loading...</div>}><ProtectedRoute requirePartner><PartnerClientOnboarding /></ProtectedRoute></Suspense>} />
        <Route path="/partner/analytics" element={<Suspense fallback={<div className="text-[#0f172a] p-8">Loading...</div>}><ProtectedRoute requirePartner><PartnerAnalytics /></ProtectedRoute></Suspense>} />
        <Route path="/partner/workout-store" element={<Suspense fallback={<div className="text-[#0f172a] p-8">Loading...</div>}><ProtectedRoute requirePartner><PartnerWorkoutStore /></ProtectedRoute></Suspense>} />
        <Route path="/partner/create-workout" element={<Suspense fallback={<div className="text-[#0f172a] p-8">Loading...</div>}><ProtectedRoute requirePartner><PartnerCreateWorkout /></ProtectedRoute></Suspense>} />
        <Route path="/partner/create-workout/:id" element={<Suspense fallback={<div className="text-[#0f172a] p-8">Loading...</div>}><ProtectedRoute requirePartner><PartnerCreateWorkout /></ProtectedRoute></Suspense>} />

        <Route path="/auth/whoop/oauth2" element={<WhoopOAuthCallback />} />

        {/* ADMIN DASHBOARD */}
        <Route path="/admin/overview" element={<Suspense fallback={<div className="text-[#0f172a] p-8">Loading...</div>}><AdminOverview /></Suspense>} />
        <Route path="/admin/engagement" element={<Suspense fallback={<div className="text-[#0f172a] p-8">Loading...</div>}><AdminEngagement /></Suspense>} />
        <Route path="/admin/features" element={<Suspense fallback={<div className="text-[#0f172a] p-8">Loading...</div>}><AdminFeatureUsage /></Suspense>} />
        <Route path="/admin/retention" element={<Suspense fallback={<div className="text-[#0f172a] p-8">Loading...</div>}><AdminRetention /></Suspense>} />
        <Route path="/admin/ai-quality" element={<Suspense fallback={<div className="text-[#0f172a] p-8">Loading...</div>}><AdminAIQuality /></Suspense>} />

        {/* CATCH ALL */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ModalProvider>
  );
}
